import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  FilePlus2,
  HeartPulse,
  Pill,
  Search,
  Stethoscope,
  Syringe,
  UserRound,
} from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";
import { formatPatientName } from "../../utils/patientName";
import { formatVisitLabel } from "../../utils/visitNumber";
import { formatBp, formatHeightParts, formatPulse, formatSpo2, formatTempF, formatWeight } from "../../utils/vitalsFormat";

interface Patient {
  id: number;
  salutation?: string;
  name?: string;
  phone?: string;
  gender?: string;
  dob?: string;
  address?: string;
  blood_group?: string;
}

const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return "";
  return String(new Date().getFullYear() - date.getFullYear());
};

const labelForStatus = (status?: string) => {
  if (!status) return "-";
  return status.toLowerCase().split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

const medicineLabel = (item: any) => {
  const medicine = item.medicine;
  const name = medicine?.brand || medicine?.name || item.medicine_name || "Medicine";
  if (medicine?.type === "Tablet") return `Tab. ${name}`;
  if (medicine?.type === "Capsule") return `Cap. ${name}`;
  return name;
};

const todayString = () => {
  const now = new Date();
  const offsetDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 10);
};

const PatientHistory: React.FC = () => {
  const { can } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultDate = todayString();
  const patientIdFromUrl = searchParams.get("patient_id");
  const visitIdFromUrl = searchParams.get("visit_id");
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientSuggestionsOpen, setPatientSuggestionsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [conditions, setConditions] = useState<any[]>([]);
  const [medications, setMedications] = useState<any[]>([]);
  const [visits, setVisits] = useState<any[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<any | null>(null);
  const [labReports, setLabReports] = useState<any[]>([]);
  const [newCondition, setNewCondition] = useState({ condition_type: "disease", name: "", details: "" });
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDate);
  const [dateTo, setDateTo] = useState(defaultDate);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const canEdit = can("patients.history.edit");

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const searchPatients = async () => {
    if (!query.trim()) return;
    setPatients(await api(`/api/patients/search?q=${encodeURIComponent(query)}`));
    setPatientSuggestionsOpen(true);
  };

  const loadPatientHistory = async (patient: Patient) => {
    setLoadingHistory(true);
    setSelectedPatient(patient);
    setQuery(formatPatientName(patient, patient.name || ""));
    setPatientSuggestionsOpen(false);
    try {
      const visitParams = new URLSearchParams({
        patient_id: String(patient.id),
        date_from: dateFrom,
        date_to: dateTo,
      });
      const [conditionData, medicationData, visitData, reportData] = await Promise.all([
        api(`/api/patient-conditions?patient_id=${patient.id}`),
        api(`/api/patient-medications?patient_id=${patient.id}`),
        api(`/api/visits?${visitParams.toString()}`),
        api(`/api/labs/reports?patient_id=${patient.id}`),
      ]);
      setConditions(conditionData);
      setMedications(medicationData);
      setVisits(visitData);
      setSelectedVisit((current: any) => {
        const linkedVisit = visitIdFromUrl ? visitData.find((visit: any) => String(visit.id) === visitIdFromUrl) : null;
        return linkedVisit || visitData.find((visit: any) => visit.id === current?.id) || visitData.find((visit: any) => visit.status === "COMPLETED") || visitData[0] || null;
      });
      setLabReports(reportData);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!query.trim() || query === formatPatientName(selectedPatient, selectedPatient?.name || "")) {
      setPatients([]);
      setPatientSuggestionsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      const results = await api(`/api/patients/search?q=${encodeURIComponent(query)}`);
      setPatients(results);
      setPatientSuggestionsOpen(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [query, selectedPatient?.name, selectedPatient?.salutation]);

  useEffect(() => {
    if (!patientIdFromUrl) return;

    const loadLinkedPatient = async () => {
      const patient = await api(`/api/patients/${patientIdFromUrl}`);
      if (patient) await loadPatientHistory(patient);
    };

    loadLinkedPatient().catch((err: any) => setMessage(`Failed to load linked patient: ${err.message}`));
  }, [patientIdFromUrl, visitIdFromUrl]);

  useEffect(() => {
    if (selectedPatient) loadPatientHistory(selectedPatient);
  }, [dateFrom, dateTo]);

  const resetToToday = () => {
    const today = todayString();
    setDateFrom(today);
    setDateTo(today);
  };

  const addCondition = async () => {
    if (!selectedPatient || !newCondition.name.trim()) return;
    await api("/api/patient-conditions", {
      method: "POST",
      body: JSON.stringify({ ...newCondition, patient_id: selectedPatient.id }),
    });
    await loadPatientHistory(selectedPatient);
    setNewCondition({ condition_type: "disease", name: "", details: "" });
    setMessage("History updated");
  };

  const addClinicalNote = async () => {
    if (!selectedPatient || !visits[0] || !note.trim()) return;
    await api("/api/clinical-records", {
      method: "POST",
      body: JSON.stringify({
        visit_id: visits[0].id,
        doctor_id: visits[0].assigned_doctor_id,
        notes: note,
      }),
    });
    setNote("");
    setMessage("Note added");
  };

  const allergies = conditions.filter((item) => item.condition_type === "allergy");
  const nonAllergyConditions = conditions.filter((item) => item.condition_type !== "allergy");
  const selectedVitals = selectedVisit?.vitals?.[0];
  const selectedClinical = selectedVisit?.clinical_record;
  const selectedPrescriptionItems = selectedVisit?.prescription?.items || [];
  const selectedServices = selectedVisit?.service_orders || [];
  const selectedLabs = selectedVisit?.lab_orders || [];
  const selectedBill = selectedVisit?.bill;
  const isCompletedVisit = selectedVisit?.status === "COMPLETED";
  const rangeLabel = dateFrom === dateTo ? dateFrom : `${dateFrom} to ${dateTo}`;

  return (
    <SidebarLayout title="Patient History" subtitle="Review live patient history, visits, medications, and lab reports.">
      <div className="grid w-full grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="flex flex-col gap-6">
          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded bg-primary/10 text-primary">
                <UserRound size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-text-light">{formatPatientName(selectedPatient, "Select Patient")}</h2>
                <p className="text-sm text-subtext-light">{selectedPatient?.phone || "Search by name or phone"}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded bg-background-light p-3">
                <div className="text-subtext-light">Age</div>
                <div className="font-bold">{calculateAge(selectedPatient?.dob) || "-"}</div>
              </div>
              <div className="rounded bg-background-light p-3">
                <div className="text-subtext-light">Sex</div>
                <div className="font-bold">{selectedPatient?.gender || "-"}</div>
              </div>
              <div className="rounded bg-background-light p-3">
                <div className="text-subtext-light">Blood</div>
                <div className="font-bold">{selectedPatient?.blood_group || "-"}</div>
              </div>
              <div className="rounded bg-background-light p-3">
                <div className="text-subtext-light">Visits</div>
                <div className="font-bold">{visits.length}</div>
              </div>
            </div>
          </section>

          <section className="rounded border border-border-light bg-surface-light p-5">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
              <AlertCircle className="text-alert-red" size={20} />
              Allergies
            </h3>
            <div className="flex flex-wrap gap-2">
              {allergies.length ? allergies.map((allergy) => (
                <span key={allergy.id} className="rounded-full bg-alert-red/10 px-3 py-1 text-sm font-semibold text-alert-red">
                  {allergy.name}
                </span>
              )) : <span className="text-sm text-subtext-light">No allergies recorded</span>}
            </div>
          </section>

          <section className="rounded border border-border-light bg-surface-light p-5">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
              <Pill className="text-primary" size={20} />
              Current Medication
            </h3>
            <div className="space-y-2">
              {medications.length ? medications.map((medication) => (
                <div key={medication.id} className="rounded bg-background-light p-3 text-sm font-semibold">
                  {medication.medicine_name}
                  <div className="text-xs font-normal text-subtext-light">{medication.dosage} {medication.frequency}</div>
                </div>
              )) : <div className="text-sm text-subtext-light">No active medications</div>}
            </div>
          </section>

          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-lg font-bold">
                <CalendarClock className="text-primary" size={20} />
                Visits
              </h3>
            </div>
            <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
              {visits.map((visit) => (
                <article
                  key={visit.id}
                  className={`rounded border p-3 transition ${selectedVisit?.id === visit.id ? "border-primary bg-primary/5" : "border-border-light bg-background-light"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-bold text-text-light">{formatVisitLabel(visit)}</h4>
                      <p className="text-xs text-subtext-light">{labelForStatus(visit.status)}</p>
                    </div>
                    {visit.status === "COMPLETED" && <span className="rounded-full bg-success-green px-2 py-1 text-xs font-semibold text-white">Done</span>}
                  </div>
                  <div className="mt-2 text-xs font-semibold text-subtle-light">{formatDate(visit.visit_date)}</div>
                  {visit.vitals?.[0] && (
                    <p className="mt-2 text-xs leading-5 text-text-light">
                      BP {formatBp(visit.vitals[0].bp_systolic, visit.vitals[0].bp_diastolic)}, PR {formatPulse(visit.vitals[0].pulse_rate)}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedVisit(visit)}
                    className="mt-3 w-full rounded border border-border-light bg-white px-3 py-2 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                  >
                    View Details
                  </button>
                </article>
              ))}
              {!visits.length && <div className="text-sm text-subtext-light">No visits found.</div>}
            </div>
          </section>
        </aside>

        <main className="flex flex-col gap-6">
          {message && <div className="rounded border border-primary/20 bg-primary/5 p-3 text-sm font-semibold text-primary">{message}</div>}
          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(240px,1fr)_minmax(0,760px)] xl:items-end">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-subtle-light">
                  <span>Patients</span>
                  <span>/</span>
                  <span className="font-semibold text-primary">Clinical History</span>
                </div>
                <h2 className="text-2xl font-black text-text-light">History Summary</h2>
                <p className="mt-1 text-sm text-subtext-light">Showing visits for {rangeLabel}</p>
              </div>
              <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(220px,1fr)_145px_145px_auto_auto]">
                <label className="relative block min-w-0">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">Patient</span>
                  <span className="relative block">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-subtext-light" size={18} />
                    <input
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setSelectedPatient(null);
                      }}
                      onFocus={() => patients.length && setPatientSuggestionsOpen(true)}
                      className="h-11 w-full rounded border border-border-light bg-background-light pl-10 pr-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                      placeholder="Search patient name / phone"
                    />
                  </span>
                  {patientSuggestionsOpen && patients.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded border border-border-light bg-white shadow-lg">
                      {patients.map((patient) => (
                        <button
                          key={patient.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => loadPatientHistory(patient)}
                          className="block w-full border-b border-border-light px-3 py-2 text-left last:border-b-0 hover:bg-primary/10"
                        >
                          <div className="font-bold text-text-light">{formatPatientName(patient, `Patient ${patient.id}`)}</div>
                          <div className="text-xs text-subtext-light">{patient.phone || "-"} {patient.address ? `- ${patient.address}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    max={dateTo}
                    onChange={(event) => setDateFrom(event.target.value)}
                    className="h-11 w-full rounded border border-border-light bg-background-light px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
                <label className="block min-w-0">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">To</span>
                  <input
                    type="date"
                    value={dateTo}
                    min={dateFrom}
                    onChange={(event) => setDateTo(event.target.value)}
                    className="h-11 w-full rounded border border-border-light bg-background-light px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
                <button
                  type="button"
                  onClick={resetToToday}
                  className="inline-flex h-11 items-center justify-center rounded border border-border-light px-4 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
                >
                  Today
                </button>
                <button onClick={searchPatients} className="inline-flex h-11 items-center justify-center gap-2 rounded bg-primary px-4 font-semibold text-white hover:bg-primary/90">
                  <Search size={18} />
                  Search
                </button>
              </div>
            </div>
            {patients.length > 0 && !patientSuggestionsOpen && (
              <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                {patients.map((patient) => (
                  <button key={patient.id} onClick={() => loadPatientHistory(patient)} className="rounded border border-border-light bg-background-light p-3 text-left">
                    <div className="font-bold">{formatPatientName(patient, `Patient ${patient.id}`)}</div>
                    <div className="text-sm text-subtext-light">{patient.phone}</div>
                  </button>
                ))}
              </div>
            )}
            {loadingHistory && <div className="mt-4 rounded bg-background-light p-3 text-sm text-subtext-light">Loading patient history...</div>}
          </section>

          {selectedVisit && (
            <section className="rounded border border-border-light bg-surface-light p-5">
              <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold uppercase tracking-wide text-subtext-light">Read Only Visit Details</span>
                    {isCompletedVisit && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success-green px-2 py-1 text-xs font-bold text-white">
                        <CheckCircle2 size={14} />
                        Completed
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-black text-text-light">{formatVisitLabel(selectedVisit)}</h3>
                  <p className="text-sm text-subtext-light">{formatDate(selectedVisit.visit_date)} - {labelForStatus(selectedVisit.status)}</p>
                </div>
                <span className="rounded bg-background-light px-3 py-2 text-sm font-bold text-subtle-light">
                  {selectedVisit.assigned_doctor?.name || "Doctor unassigned"}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 rounded bg-background-light p-3 text-sm md:grid-cols-6">
                <div><span className="text-subtext-light">BP</span><div className="font-bold">{formatBp(selectedVitals?.bp_systolic, selectedVitals?.bp_diastolic)}</div></div>
                <div><span className="text-subtext-light">PR</span><div className="font-bold">{formatPulse(selectedVitals?.pulse_rate)}</div></div>
                <div><span className="text-subtext-light">SpO2</span><div className="font-bold">{formatSpo2(selectedVitals?.oxygen_saturation)}</div></div>
                <div><span className="text-subtext-light">Temp</span><div className="font-bold">{formatTempF(selectedVitals?.temperature)}</div></div>
                {(selectedVitals?.height || selectedVitals?.height_feet || selectedVitals?.height_inches) && (
                  <div><span className="text-subtext-light">Height</span><div className="font-bold">{formatHeightParts(selectedVitals?.height_feet, selectedVitals?.height_inches, selectedVitals?.height)}</div></div>
                )}
                <div><span className="text-subtext-light">Weight</span><div className="font-bold">{formatWeight(selectedVitals?.weight)}</div></div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">Chief Complaint</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.chief_complaint || "-"}</p>
                </div>
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">Allergic</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.allergic || "-"}</p>
                </div>
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">Allergy History</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.allergy_history || "-"}</p>
                </div>
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">Medication History</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.medication_history || "-"}</p>
                </div>
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">O/E Notes</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.notes || "-"}</p>
                </div>
                <div className="rounded border border-border-light bg-background-light p-4">
                  <div className="mb-1 text-sm font-bold uppercase text-subtext-light">Advice</div>
                  <p className="text-sm leading-6 text-text-light">{selectedClinical?.follow_up_notes || "-"}</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
                <section className="rounded border border-border-light bg-background-light p-4">
                  <h4 className="mb-3 font-bold">Prescription</h4>
                  <div className="space-y-2">
                    {selectedPrescriptionItems.length ? selectedPrescriptionItems.map((item: any, index: number) => (
                      <div key={item.id || index} className="rounded bg-white p-3 text-sm">
                        <div className="font-semibold">{medicineLabel(item)}</div>
                        <div className="text-xs text-subtext-light">{[item.dosage, item.frequency, item.duration, item.instructions].filter(Boolean).join(" - ") || "-"}</div>
                      </div>
                    )) : <div className="text-sm text-subtext-light">No prescription items.</div>}
                  </div>
                </section>

                <section className="rounded border border-border-light bg-background-light p-4">
                  <h4 className="mb-3 font-bold">Services</h4>
                  <div className="space-y-2">
                    {selectedServices.length ? selectedServices.map((service: any) => (
                      <div key={service.id} className="rounded bg-white p-3 text-sm font-semibold">
                        {service.service_name}
                        <div className="text-xs font-normal text-subtext-light">Rs {service.price || 0}</div>
                      </div>
                    )) : <div className="text-sm text-subtext-light">No services.</div>}
                  </div>
                </section>

                <section className="rounded border border-border-light bg-background-light p-4">
                  <h4 className="mb-3 font-bold">Labs / Bill</h4>
                  <div className="space-y-2">
                    {selectedLabs.map((lab: any) => (
                      <div key={lab.id} className="rounded bg-white p-3 text-sm font-semibold">{lab.test_name}</div>
                    ))}
                    {selectedBill && (
                      <div className="rounded bg-white p-3 text-sm">
                        <div className="font-semibold">Bill #{selectedBill.id}</div>
                        <div className="text-xs text-subtext-light">Total: Rs {Number(selectedBill.totalAmount || 0).toFixed(2)}</div>
                      </div>
                    )}
                    {!selectedLabs.length && !selectedBill && <div className="text-sm text-subtext-light">No labs or bill.</div>}
                  </div>
                </section>
              </div>
            </section>
          )}

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {nonAllergyConditions.map((condition) => {
              const Icon = condition.condition_type === "surgery" ? Syringe : HeartPulse;
              return (
                <div key={condition.id} className="rounded border border-border-light bg-surface-light p-5">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded bg-primary/10 text-primary">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-bold">{condition.name}</h3>
                  <p className="mt-1 text-sm leading-6 text-subtext-light">{condition.details || condition.status}</p>
                </div>
              );
            })}
            {!nonAllergyConditions.length && <div className="rounded border border-border-light bg-surface-light p-5 text-sm text-subtext-light">No conditions recorded.</div>}
          </section>

          {canEdit && selectedPatient && (
            <section className="rounded border border-primary/20 bg-primary/5 p-5">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
                <FilePlus2 className="text-primary" size={20} />
                Add History Item
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_1fr_auto]">
                <select value={newCondition.condition_type} onChange={(e) => setNewCondition((prev) => ({ ...prev, condition_type: e.target.value }))} className="rounded border border-border-light bg-white p-3">
                  <option value="disease">Disease</option>
                  <option value="surgery">Surgery</option>
                  <option value="allergy">Allergy</option>
                  <option value="chronic_condition">Chronic</option>
                </select>
                <input value={newCondition.name} onChange={(e) => setNewCondition((prev) => ({ ...prev, name: e.target.value }))} className="rounded border border-border-light bg-white p-3" placeholder="Name" />
                <input value={newCondition.details} onChange={(e) => setNewCondition((prev) => ({ ...prev, details: e.target.value }))} className="rounded border border-border-light bg-white p-3" placeholder="Details" />
                <button onClick={addCondition} className="rounded bg-primary px-4 py-2 font-semibold text-white">Add</button>
              </div>
            </section>
          )}

          <section className="rounded border border-border-light bg-surface-light p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
              <Stethoscope className="text-primary" size={20} />
              Lab Reports
            </h3>
            <div className="space-y-2">
              {labReports.map((report) => (
                <div key={report.id} className="rounded bg-background-light p-3 text-sm">
                  <div className="font-semibold">{report.file_name || `Report #${report.id}`}</div>
                  <div className="text-subtext-light">{report.result_summary || report.status}</div>
                </div>
              ))}
              {!labReports.length && <div className="text-sm text-subtext-light">No lab reports uploaded.</div>}
            </div>
          </section>

          {canEdit && selectedPatient && visits[0] && (
            <section className="rounded border border-primary/20 bg-primary/5 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
                <Stethoscope className="text-primary" size={20} />
                Clinical Note
              </h3>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-28 w-full resize-none rounded border border-border-light bg-white p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              <button onClick={addClinicalNote} className="mt-3 rounded bg-primary px-4 py-2 font-semibold text-white">Add Note</button>
            </section>
          )}
        </main>
      </div>
    </SidebarLayout>
  );
};

export default PatientHistory;
